import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { WizardModalLayout } from '../../components/ModalScrollProgress';
import {
  getBookingWizardModalProps,
  StyledModalCardV2
} from '../../components/modalV2';
import WizardChromeV2 from '../../components/WizardV2/WizardChromeV2';
import {
  WizardFooterButtonPrimary,
  WizardFooterButtonSecondary
} from '../../components/WizardV2/wizardStylesV2';
import {
  ModalBodyContent,
  ModalButtonsContainer,
  ModalHeader,
  SectionContainer,
  StyledModalBody,
  StyledModalV2
} from '../../components/Modals';
import { Flex } from '../../components/ui/Flex';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../components/ui/Form';
import { ButtonBodyVariant } from '../../components/ui/ButtonVariant';
import { changeOpenedModal } from '../../redux/actions/headerModal';
import { useScrollToBottom } from '../../hooks/useScrollToBottom';
import {
  canChooseDocOrgScope,
  canChooseDocOrgWideScope,
  canFolderHoldDoc,
  canManageDocument,
  DOC_ACCESS,
  DOC_SCOPE,
  DOCUMENT_MAX_FILE_SIZE_ERROR,
  DOCUMENT_MAX_VIDEO_SIZE_ERROR,
  DOCUMENT_ROOT_FOLDER_VALUE,
  DOCUMENT_VIDEO_CONTENT_TYPE,
  folderSelectValueToId,
  getDocumentMaxSizeBytes,
  getDocumentNameFromFile,
  isDocumentVideoFile,
  isFolderScopeMismatchError,
  isGlobalDocVisibility,
  normalizeFolderSelectValue,
  parseDocVisibility,
  resolveDocVisibility,
  RESOURCE_CENTER_FILE_TYPE
} from './constants';
import {
  getDocuments,
  getFolderDocuments,
  getFolderOptions,
  updateDocument,
  uploadDocument
} from '../../redux/actions/documents';
import { setbuildingImg } from '../../redux/actions/building';
import { setToast } from '../../redux/actions/window';
import { requestPending } from '../../utils/status';
import {
  GET_FOLDER_OPTIONS,
  UPDATE_DOCUMENT,
  UPLOAD_DOCUMENT
} from '../../redux/constants';
import { UserRole } from '../users/constant';
import { getAddDocumentWizardChromeProps } from './documentWizardConfig';
import AddDocumentUploadStep from './AddDocumentUploadStep';
import AddDocumentVisibilityStep from './AddDocumentVisibilityStep';
import { DocumentNameInput } from './documentWizardStyles';

const hasDocumentStorageEnabled = (building) => !!building?.config?.enable_document_storage;

const buildDocumentFormData = ({
  file,
  orgId,
  buildingId,
  name,
  visibility,
  folderId,
  thumbnail = ''
}) => {
  const formData = new FormData();
  if (file) {
    formData.append('doc', file);
  }
  formData.append('type', RESOURCE_CENTER_FILE_TYPE);
  formData.append('org', orgId);
  if (!isGlobalDocVisibility(visibility) && buildingId) {
    formData.append('building', buildingId);
  }
  formData.append('name', name);
  formData.append('visibility', visibility);
  if (folderId !== undefined) {
    formData.append('folder_id', folderId == null ? 0 : folderId);
  }
  formData.append('thumbnail', thumbnail);
  return formData;
};

const getDocumentIdFromUploadResponse = (response) =>
  response?.data?.new_id ?? response?.data?.id ?? null;

const buildVideoUploadBody = ({ file, orgId, buildingId, name, visibility, folderId }) => {
  const body = {
    type: RESOURCE_CENTER_FILE_TYPE,
    org: Number(orgId),
    name,
    visibility: Number(visibility),
    filename: file.name,
    content_type: DOCUMENT_VIDEO_CONTENT_TYPE,
    size_bytes: file.size
  };

  if (!isGlobalDocVisibility(visibility) && buildingId) {
    body.building = Number(buildingId);
  } else {
    body.building = 0;
  }

  if (folderId !== undefined) {
    body.folder_id = folderId == null ? 0 : Number(folderId);
  }

  return body;
};

const AddEditDocumentModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const modalBodyRef = useRef(null);
  const { isAtBottom } = useScrollToBottom(modalBodyRef);
  const modal = useSelector((state) => state.headerModal.modal);
  const modalData = useSelector((state) => state.headerModal.data);
  const globalOrgId = useSelector((state) => state.Organization.globalOrgId);
  const organization = useSelector((state) => state.Organization.organization);
  const globalBuildingId = useSelector((state) => state.building.globalBuildingId);
  const allBuildings = useSelector((state) => state.building.allBuildings);
  const roleId = useSelector((state) => state.auth.roleId);
  const {
    uploadDocumentStatus,
    updateDocumentStatus,
    uploadProgress,
    uploadStalled,
    lastListParams,
    lastListMode,
    lastFolderId,
    folderOptions,
    getFolderOptionsStatus
  } = useSelector((state) => state.documents);
  const [wizardStep, setWizardStep] = useState(0);
  const [thumbnailRemoved, setThumbnailRemoved] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isAddSubmitting, setIsAddSubmitting] = useState(false);

  const isUploading = uploadDocumentStatus === requestPending(UPLOAD_DOCUMENT);
  const isSubmitting =
    isUploading || updateDocumentStatus === requestPending(UPDATE_DOCUMENT);
  const isEditSaveBusy = isEditSubmitting;
  const isAddSaveBusy = isAddSubmitting || isSubmitting;

  const foldersLoading = getFolderOptionsStatus === requestPending(GET_FOLDER_OPTIONS);

  const uploadThumbnailToMedia = (thumbnailField) =>
    new Promise((resolve, reject) => {
      if (!thumbnailField?.blob) {
        resolve(null);
        return;
      }
      const imageData = new FormData();
      imageData.append('image', thumbnailField.blob, thumbnailField.fileName || 'thumbnail.png');
      dispatch(
        setbuildingImg({
          body: imageData,
          success: (res) => resolve(res?.data?.filename || null),
          fails: () => reject(new Error('Thumbnail upload failed'))
        })
      );
    });

  const resolveDocumentThumbnailForSave = async ({
    documentThumbnail,
    existingThumbnail,
    thumbnailRemoved: removed
  }) => {
    if (documentThumbnail?.blob) {
      const uploaded = await uploadThumbnailToMedia(documentThumbnail);
      if (!uploaded) {
        throw new Error('Thumbnail upload did not return a URL');
      }
      return uploaded;
    }
    if (removed) {
      return '';
    }
    return existingThumbnail || '';
  };

  const isEdit = modal === 'addEditDocument' && Boolean(modalData?.id);
  const showAddWizard = modal === 'addEditDocument' && !isEdit;
  const orgId = globalOrgId || organization?.id;
  const accessibleBuildingIds = (allBuildings || []).map((building) => building.id);
  if (
    globalBuildingId > 0 &&
    !accessibleBuildingIds.some((id) => Number(id) === Number(globalBuildingId))
  ) {
    accessibleBuildingIds.push(globalBuildingId);
  }
  const canEditDocument =
    !isEdit || canManageDocument(modalData, roleId, accessibleBuildingIds);

  const documentStorageBuildings = useMemo(
    () => (allBuildings || []).filter(hasDocumentStorageEnabled),
    [allBuildings]
  );

  // Edit: keep the document's current building selectable even if storage was turned off.
  const buildingsForScopeSelect = useMemo(() => {
    if (!isEdit || !modalData) {
      return documentStorageBuildings;
    }

    const currentId = modalData.building ?? modalData.building_id;
    if (!currentId) {
      return documentStorageBuildings;
    }

    if (documentStorageBuildings.some((building) => Number(building.id) === Number(currentId))) {
      return documentStorageBuildings;
    }

    const currentBuilding = (allBuildings || []).find(
      (building) => Number(building.id) === Number(currentId)
    );
    return currentBuilding
      ? [...documentStorageBuildings, currentBuilding]
      : documentStorageBuildings;
  }, [allBuildings, documentStorageBuildings, isEdit, modalData]);

  const showScopeOptions = canChooseDocOrgScope(roleId, documentStorageBuildings.length || 0);
  const canChooseOrgWide = canChooseDocOrgWideScope(roleId);
  const defaultBuildingId = useMemo(() => {
    if (!(globalBuildingId > 0)) {
      return null;
    }
    const globalHasStorage = documentStorageBuildings.some(
      (building) => Number(building.id) === Number(globalBuildingId)
    );
    return globalHasStorage ? globalBuildingId : null;
  }, [documentStorageBuildings, globalBuildingId]);

  const schema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(t('Name is required', { defaultValue: 'Name is required' }))
      .max(255, t('Name is too long', { defaultValue: 'Name is too long' })),
    scope: Yup.string().required(t('Applies to is required', { defaultValue: 'Applies to is required' })),
    access: Yup.string().required(t('Visible to is required', { defaultValue: 'Visible to is required' })),
    buildingId: Yup.mixed().test('building-required', t('Building is required'), function (value) {
      if (!this.options.context?.requireBuildingSelect) {
        return true;
      }
      if (this.parent.scope !== DOC_SCOPE.BUILDING) {
        return true;
      }
      return Boolean(value) && Number(value) > 0;
    }),
    file: Yup.mixed()
      .test('file-required', t('File is required'), function (value) {
        if (this.options.context?.isEdit) {
          return true;
        }
        return value instanceof File;
      })
      .test('file-size', function (value) {
        if (!(value instanceof File)) {
          return true;
        }
        if (value.size <= getDocumentMaxSizeBytes(value)) {
          return true;
        }
        return this.createError({
          message: t(
            isDocumentVideoFile(value)
              ? DOCUMENT_MAX_VIDEO_SIZE_ERROR
              : DOCUMENT_MAX_FILE_SIZE_ERROR
          )
        });
      }),
    folderId: Yup.mixed().nullable()
  });

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
    context: { isEdit, requireBuildingSelect: showScopeOptions },
    defaultValues: {
      name: '',
      scope: DOC_SCOPE.BUILDING,
      access: DOC_ACCESS.RESIDENTS_AND_ADMIN,
      buildingId: defaultBuildingId,
      file: null,
      folderId: DOCUMENT_ROOT_FOLDER_VALUE,
      document_thumbnail: null
    }
  });

  const file = useWatch({ control: form.control, name: 'file' });
  const watchedBuildingId = useWatch({ control: form.control, name: 'buildingId' });
  const watchedScope = useWatch({ control: form.control, name: 'scope' });

  const folderPickerBuildingId = useMemo(() => {
    if (watchedScope === DOC_SCOPE.ORG) {
      return globalBuildingId > 0 ? globalBuildingId : null;
    }
    if (showScopeOptions) {
      return watchedBuildingId > 0 ? watchedBuildingId : null;
    }
    return globalBuildingId > 0 ? globalBuildingId : null;
  }, [watchedScope, showScopeOptions, watchedBuildingId, globalBuildingId]);

  const isOrgWideSelection = watchedScope === DOC_SCOPE.ORG;

  // The building the file will belong to once saved; 0 for org-wide files.
  const docBuildingId = useMemo(() => {
    if (isOrgWideSelection) {
      return 0;
    }
    return showScopeOptions
      ? Number(watchedBuildingId) || 0
      : Number(globalBuildingId) || 0;
  }, [isOrgWideSelection, showScopeOptions, watchedBuildingId, globalBuildingId]);

  const folderScope = useMemo(
    () => ({ isOrgWide: isOrgWideSelection, buildingId: docBuildingId }),
    [isOrgWideSelection, docBuildingId]
  );

  const foldersForSelect = useMemo(() => {
    const options = (folderOptions || []).filter((folder) =>
      canFolderHoldDoc(folder, folderScope)
    );

    // Keep an unknown current folder only while the file's original scope still matches,
    // so a scope change cannot leave a stale destination in the picker.
    if (isEdit && modalData?.folder_id > 0) {
      const known = (folderOptions || []).some(
        (folder) => Number(folder.id) === Number(modalData.folder_id)
      );
      if (!known) {
        const originalIsOrgWide = isGlobalDocVisibility(modalData.visibility);
        const originalBuildingId = modalData.building ?? modalData.building_id;
        const stillMatches =
          originalIsOrgWide === folderScope.isOrgWide &&
          (folderScope.isOrgWide ||
            Number(originalBuildingId) === Number(folderScope.buildingId));
        if (stillMatches) {
          options.unshift({
            id: modalData.folder_id,
            name: t('Current folder', { defaultValue: 'Current folder' }),
            shared: false
          });
        }
      }
    }

    return options;
  }, [folderOptions, isEdit, modalData, t, folderScope]);

  // Drop a selected folder that no longer matches the file's scope, so we never
  // send a folder_id the API would reject.
  useEffect(() => {
    const selectedFolderId = folderSelectValueToId(form.getValues('folderId'));
    if (!selectedFolderId) {
      return;
    }

    const selectedFolder = (folderOptions || []).find(
      (folder) => Number(folder.id) === Number(selectedFolderId)
    );
    if (selectedFolder) {
      if (canFolderHoldDoc(selectedFolder, folderScope)) {
        return;
      }
      form.setValue('folderId', DOCUMENT_ROOT_FOLDER_VALUE, {
        shouldDirty: true,
        shouldValidate: true
      });
      return;
    }

    if (foldersLoading) {
      return;
    }

    const isCurrentUnknownFolder =
      isEdit && Number(modalData?.folder_id) === Number(selectedFolderId);
    if (isCurrentUnknownFolder) {
      const originalIsOrgWide = isGlobalDocVisibility(modalData.visibility);
      const originalBuildingId = modalData.building ?? modalData.building_id;
      const stillMatches =
        originalIsOrgWide === folderScope.isOrgWide &&
        (folderScope.isOrgWide ||
          Number(originalBuildingId) === Number(folderScope.buildingId));
      if (stillMatches) {
        return;
      }
    }

    form.setValue('folderId', DOCUMENT_ROOT_FOLDER_VALUE, {
      shouldDirty: true,
      shouldValidate: true
    });
  }, [folderOptions, folderScope, foldersLoading, form, isEdit, modalData]);

  const initialFolderId = useMemo(() => {
    if (isEdit && modalData) {
      return normalizeFolderSelectValue(modalData.folder_id);
    }
    if (modalData?.defaultFolderId > 0) {
      return normalizeFolderSelectValue(modalData.defaultFolderId);
    }
    if (lastFolderId > 0) {
      return normalizeFolderSelectValue(lastFolderId);
    }
    return DOCUMENT_ROOT_FOLDER_VALUE;
  }, [isEdit, modalData, lastFolderId]);

  useEffect(() => {
    if (!(showAddWizard || isEdit) || !orgId || !(folderPickerBuildingId > 0)) {
      return;
    }

    const params = {
      building: folderPickerBuildingId
    };
    if (roleId === UserRole.SUPER_ADMIN) {
      params.org = orgId;
    }

    dispatch(getFolderOptions({ params }));
  }, [showAddWizard, isEdit, orgId, folderPickerBuildingId, roleId, dispatch]);

  useEffect(() => {
    if (isEdit && modalData && !canEditDocument) {
      dispatch(
        changeOpenedModal({
          modal: 'viewDocumentModal',
          data: modalData
        })
      );
    }
  }, [isEdit, modalData, canEditDocument, dispatch]);

  useEffect(() => {
    if (isEdit && modalData) {
      const { scope, access } = parseDocVisibility(modalData.visibility);
      // Multi-building managers can see scope but not org-wide; coerce so the UI stays valid.
      const resolvedScope =
        scope === DOC_SCOPE.ORG && showScopeOptions && !canChooseOrgWide
          ? DOC_SCOPE.BUILDING
          : scope;

      form.reset({
        name: modalData.name || '',
        scope: resolvedScope,
        access,
        buildingId: modalData.building || modalData.building_id || defaultBuildingId,
        file: null,
        folderId: normalizeFolderSelectValue(modalData.folder_id),
        document_thumbnail: null
      });
      setThumbnailRemoved(false);
      setIsEditSubmitting(false);
      setIsAddSubmitting(false);
      setWizardStep(0);
    } else if (showAddWizard) {
      form.reset({
        name: '',
        scope: DOC_SCOPE.BUILDING,
        access: DOC_ACCESS.RESIDENTS_AND_ADMIN,
        buildingId: defaultBuildingId,
        file: null,
        folderId: initialFolderId,
        document_thumbnail: null
      });
      setThumbnailRemoved(false);
      setIsEditSubmitting(false);
      setIsAddSubmitting(false);
      setWizardStep(0);
    }
  }, [
    isEdit,
    showAddWizard,
    modalData,
    form,
    defaultBuildingId,
    canChooseOrgWide,
    showScopeOptions,
    initialFolderId
  ]);

  const closeModal = () => {
    dispatch(changeOpenedModal({ modal: '' }));
    setWizardStep(0);
    setThumbnailRemoved(false);
  };

  const handleUserClose = () => {
    if (isSubmitting || isAddSubmitting || isEditSubmitting) {
      return;
    }
    closeModal();
  };

  // Same pattern as other tables: refetch the current list query (filters included).
  const refreshDocuments = () => {
    if (lastListMode === 'folder' && lastFolderId) {
      dispatch(
        getFolderDocuments({
          id: lastFolderId,
          params: { ...(lastListParams || {}) }
        })
      );
      return;
    }

    if (lastListParams) {
      dispatch(getDocuments({ params: { ...lastListParams } }));
      return;
    }

    if (!orgId || !(globalBuildingId > 0)) {
      return;
    }

    const params = {
      type: RESOURCE_CENTER_FILE_TYPE,
      page: 1,
      building: globalBuildingId
    };

    if (roleId === UserRole.SUPER_ADMIN) {
      params.org = orgId;
    }

    dispatch(getDocuments({ params }));
  };

  const resolveFolderIdForApi = (folderId) => folderSelectValueToId(folderId);

  const folderScopeFailMessage = (err, fallback) =>
    isFolderScopeMismatchError(err)
      ? t(
          'This file does not fit that folder. Org-wide files and files from other buildings can only be placed in shared folders.',
          {
            defaultValue:
              'This file does not fit that folder. Org-wide files and files from other buildings can only be placed in shared folders.'
          }
        )
      : fallback;

  const onSubmit = (values) => {
    const visibility = resolveDocVisibility(values.scope, values.access);
    const name =
      values.name?.trim() ||
      getDocumentNameFromFile(values.file) ||
      values.file?.name ||
      modalData?.name ||
      '';

    const selectedBuildingId =
      values.scope === DOC_SCOPE.BUILDING
        ? showScopeOptions
          ? values.buildingId
          : globalBuildingId
        : null;

    let targetFolderId = resolveFolderIdForApi(values.folderId);
    const targetFolder = (folderOptions || []).find(
      (folder) => Number(folder.id) === Number(targetFolderId)
    );
    if (
      targetFolder &&
      !canFolderHoldDoc(targetFolder, {
        isOrgWide: values.scope === DOC_SCOPE.ORG,
        buildingId: selectedBuildingId || 0
      })
    ) {
      targetFolderId = null;
    }

    const metadataPayload = {
      file: null,
      orgId,
      buildingId: selectedBuildingId,
      name,
      visibility,
      folderId: targetFolderId
    };

    if (isEdit) {
      if (!canManageDocument(modalData, roleId, accessibleBuildingIds)) {
        return;
      }

      const runEdit = async () => {
        setIsEditSubmitting(true);
        let thumbnail = modalData?.thumbnail || modalData?.thumbnail_url || '';
        let thumbFailed = false;
        try {
          thumbnail = await resolveDocumentThumbnailForSave({
            documentThumbnail: values.document_thumbnail,
            existingThumbnail: modalData?.thumbnail || modalData?.thumbnail_url,
            thumbnailRemoved
          });
        } catch {
          thumbFailed = true;
          thumbnail = modalData?.thumbnail || modalData?.thumbnail_url || '';
        }

        dispatch(
          updateDocument({
            id: modalData.id,
            body: buildDocumentFormData({
              ...metadataPayload,
              thumbnail
            }),
            success: () => {
              setIsEditSubmitting(false);
              dispatch(
                setToast({
                  toastShow: true,
                  toastMessage: thumbFailed
                    ? t('Document updated, but thumbnail upload failed. Try again from edit.', {
                        defaultValue:
                          'Document updated, but thumbnail upload failed. Try again from edit.'
                      })
                    : t('Document updated successfully')
                })
              );
              refreshDocuments();
              closeModal();
            },
            fail: (err) => {
              setIsEditSubmitting(false);
              dispatch(
                setToast({
                  toastShow: true,
                  toastMessage: folderScopeFailMessage(err, t('Failed to update document')),
                  modal: 'errorToast'
                })
              );
            }
          })
        );
      };

      runEdit();
      return;
    }

    const onUploadSuccess = async (response) => {
      try {
        const documentId = getDocumentIdFromUploadResponse(response);
        let thumbFailed = false;
        if (documentId && values.document_thumbnail?.blob) {
          try {
            const thumbnail = await resolveDocumentThumbnailForSave({
              documentThumbnail: values.document_thumbnail,
              existingThumbnail: '',
              thumbnailRemoved: false
            });
            await new Promise((resolve, reject) => {
              dispatch(
                updateDocument({
                  id: documentId,
                  body: buildDocumentFormData({
                    ...metadataPayload,
                    thumbnail
                  }),
                  success: resolve,
                  fail: reject
                })
              );
            });
          } catch {
            thumbFailed = true;
          }
        }

        dispatch(
          setToast({
            toastShow: true,
            toastMessage: thumbFailed
              ? t('Document uploaded, but thumbnail could not be saved. Edit the document to retry.', {
                  defaultValue:
                    'Document uploaded, but thumbnail could not be saved. Edit the document to retry.'
                })
              : t('Document uploaded successfully')
          })
        );
        refreshDocuments();
        closeModal();
      } finally {
        setIsAddSubmitting(false);
      }
    };

    const onUploadFail = (err) => {
      setIsAddSubmitting(false);
      const isStalled =
        err?.code === 'UPLOAD_STALLED' ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('stalled'));

      dispatch(
        setToast({
          toastShow: true,
          toastMessage: isStalled
            ? t('Upload stalled. Please check your connection and try again.', {
                defaultValue: 'Upload stalled. Please check your connection and try again.'
              })
            : folderScopeFailMessage(err, t('Failed to upload document')),
          modal: 'errorToast'
        })
      );
    };

    const isVideo = isDocumentVideoFile(values.file);
    setIsAddSubmitting(true);
    dispatch(
      uploadDocument({
        file: values.file,
        body: isVideo
          ? buildVideoUploadBody({
              file: values.file,
              orgId,
              buildingId: selectedBuildingId,
              name,
              visibility,
              folderId: targetFolderId
            })
          : buildDocumentFormData({
              file: values.file,
              orgId,
              buildingId: selectedBuildingId,
              name,
              visibility,
              folderId: targetFolderId
            }),
        success: onUploadSuccess,
        fail: onUploadFail
      })
    );
  };

  const handleNext = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const ok = await form.trigger(['file'], { shouldFocus: true });
    if (ok) {
      setWizardStep(1);
    }
  };

  const handleSaveFile = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (wizardStep !== 1) {
      return;
    }
    form.handleSubmit(onSubmit)();
  };

  const handleFileChange = (nextFile) => {
    form.setValue('file', nextFile, { shouldValidate: true, shouldDirty: true });
    if (nextFile) {
      form.setValue('name', getDocumentNameFromFile(nextFile), {
        shouldValidate: false,
        shouldDirty: true
      });
    }
  };

  const folderHint = isOrgWideSelection
    ? !foldersLoading && foldersForSelect.length === 0
      ? t('No shared folders available.', {
          defaultValue: 'No shared folders available.'
        })
      : t('Org-wide files can only be placed in shared folders.', {
          defaultValue: 'Org-wide files can only be placed in shared folders.'
        })
    : t('Only folders available to the selected building are listed.', {
        defaultValue: 'Only folders available to the selected building are listed.'
      });

  const handleDownload = () => {
    if (modalData?.url) {
      window.open(modalData.url, '_blank', 'noopener,noreferrer');
    }
  };

  if (isEdit) {
    if (!canEditDocument) {
      return null;
    }

    const existingFileName = modalData?.file_name || modalData?.name;

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <StyledModalV2 show={modal === 'addEditDocument'} side width="550px">
            <StyledModalBody innerRef={(el) => (modalBodyRef.current = el)}>
              <ModalHeader title={t('Edit Document')} v2 />
              <ModalBodyContent>
                <SectionContainer>
                  <Flex direction="column" gap="16px">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem alt>
                          <FormLabel>{t('Name', { defaultValue: 'Name' })}</FormLabel>
                          <FormControl>
                            <DocumentNameInput
                              {...field}
                              placeholder={t('Document name', {
                                defaultValue: 'Document name'
                              })}
                              maxLength={255}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </Flex>
                </SectionContainer>

                <SectionContainer>
                  <AddDocumentVisibilityStep
                    t={t}
                    form={form}
                    existingFileName={existingFileName}
                    fileSizeBytes={modalData?.file_size ?? modalData?.size}
                    showThumbnail
                    isUploading={isEditSaveBusy}
                    existingThumbnailUrl={modalData?.thumbnail || modalData?.thumbnail_url}
                    thumbnailRemoved={thumbnailRemoved}
                    thumbnailRemountKey={`edit-${modalData?.id ?? 'doc'}-${thumbnailRemoved}`}
                    onRemoveThumbnail={() => {
                      setThumbnailRemoved(true);
                      form.setValue('document_thumbnail', null);
                    }}
                    onRestoreThumbnail={() => {
                      setThumbnailRemoved(false);
                      form.setValue('document_thumbnail', null);
                    }}
                    showScopeOptions={showScopeOptions}
                    canChooseOrgWide={canChooseOrgWide}
                    buildings={buildingsForScopeSelect}
                    showFolderSelect
                    folders={foldersForSelect}
                    foldersLoading={foldersLoading}
                    folderHint={folderHint}
                  />
                </SectionContainer>
              </ModalBodyContent>
            </StyledModalBody>

            <ModalButtonsContainer gapY="8px" justify="flex-end" isAtBottom={isAtBottom}>
              <ButtonBodyVariant
                type="submit"
                disabled={isSubmitting || isEditSaveBusy || !form.formState.isValid}>
                {isEditSaveBusy || updateDocumentStatus === requestPending(UPDATE_DOCUMENT)
                  ? t('Saving…', { defaultValue: 'Saving…' })
                  : t('Save changes', { defaultValue: 'Save changes' })}
              </ButtonBodyVariant>
              <ButtonBodyVariant
                variant="secondary"
                type="button"
                onClick={handleDownload}
                disabled={!modalData?.url}>
                {t('Download', { defaultValue: 'Download' })}
              </ButtonBodyVariant>
            </ModalButtonsContainer>
          </StyledModalV2>
        </form>
      </Form>
    );
  }

  if (!showAddWizard) {
    return null;
  }

  const safeStep = Math.min(Math.max(0, wizardStep), 1);
  const wizardChromeProps = getAddDocumentWizardChromeProps(safeStep, t);

  return (
    <Form {...form}>
      <form
        name="AddDocumentForm"
        onSubmit={(event) => {
          // Never submit via native form submit / Enter. Upload only from Save file click.
          event.preventDefault();
        }}>
        <WizardModalLayout
          Modal={StyledModalCardV2}
          modalProps={getBookingWizardModalProps(safeStep === 1)}
          show={showAddWizard}
          closeOnBackdrop={false}
          onClose={handleUserClose}
          width="520px"
          bodyGap="16px"
          deps={[wizardStep, showAddWizard]}
          footerActions={
            safeStep === 0 ? (
              <>
                <WizardFooterButtonSecondary type="button" onClick={handleUserClose}>
                  {t('Cancel')}
                </WizardFooterButtonSecondary>
                <WizardFooterButtonPrimary type="button" onClick={handleNext} disabled={!file}>
                  {t('Next', { defaultValue: 'Next' })}
                </WizardFooterButtonPrimary>
              </>
            ) : (
              <>
                <WizardFooterButtonSecondary
                  type="button"
                  onClick={handleUserClose}
                  disabled={isAddSaveBusy}>
                  {t('Cancel')}
                </WizardFooterButtonSecondary>
                <WizardFooterButtonPrimary
                  type="button"
                  disabled={isAddSaveBusy || !form.formState.isValid}
                  onClick={handleSaveFile}>
                  {isAddSaveBusy
                    ? isUploading && typeof uploadProgress === 'number'
                      ? t('Uploading {{percent}}%', {
                          defaultValue: `Uploading ${Math.round(uploadProgress * 100)}%`,
                          percent: Math.round(uploadProgress * 100)
                        })
                      : t('Uploading…', { defaultValue: 'Uploading…' })
                    : t('Save file', { defaultValue: 'Save file' })}
                </WizardFooterButtonPrimary>
              </>
            )
          }>
          <WizardChromeV2 {...wizardChromeProps}>
            {safeStep === 0 && (
              <AddDocumentUploadStep t={t} form={form} file={file} onFileChange={handleFileChange} />
            )}
            {safeStep === 1 && (
              <AddDocumentVisibilityStep
                t={t}
                form={form}
                file={file}
                showThumbnail
                isUploading={isUploading || isAddSubmitting}
                uploadProgress={isUploading ? uploadProgress : null}
                uploadStalled={isUploading ? uploadStalled : false}
                onClearFile={() => {
                  if (isAddSaveBusy) {
                    return;
                  }
                  handleFileChange(null);
                  setWizardStep(0);
                }}
                showScopeOptions={showScopeOptions}
                canChooseOrgWide={canChooseOrgWide}
                buildings={buildingsForScopeSelect}
                showFolderSelect
                folders={foldersForSelect}
                foldersLoading={foldersLoading}
                folderHint={folderHint}
              />
            )}
          </WizardChromeV2>
        </WizardModalLayout>
      </form>
    </Form>
  );
};

export default AddEditDocumentModal;
